import type { ReactNode } from 'react';
import { findScriptureMatches, buildScriptureHref, type ScriptureMatch } from './reference-linker';
import {
  findConfessionMatches,
  findConfession,
  type ConfessionMatch,
} from '../confessional/confessions';
import { Box, Popover, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { listAvailableBooks } from './rv1960-stub';

type Marker =
  | { kind: 'scripture'; match: ScriptureMatch }
  | { kind: 'confession'; match: ConfessionMatch };

/**
 * Renders a plain string with both Bible references (linkified to the
 * scripture reader) and confessional references (popover with the entry
 * text). Use this for any free-text field that may contain either kind of
 * reference: journal entries, prayer requests, page bodies, assignment
 * descriptions.
 */
export function RichSpiritualText({ children }: { children: string }): JSX.Element {
  const text = children;
  const scriptureMatches = findScriptureMatches(text);
  const confessionMatches = findConfessionMatches(text);
  const all: Marker[] = [
    ...scriptureMatches.map((m): Marker => ({ kind: 'scripture', match: m })),
    ...confessionMatches.map((m): Marker => ({ kind: 'confession', match: m })),
  ].sort((a, b) => a.match.start - b.match.start);

  // Drop overlapping matches (keep the first)
  const filtered: Marker[] = [];
  let lastEnd = -1;
  for (const m of all) {
    if (m.match.start >= lastEnd) {
      filtered.push(m);
      lastEnd = m.match.end;
    }
  }

  if (filtered.length === 0) return <>{text}</>;

  const available = new Set(listAvailableBooks().map((b) => b.toLowerCase()));
  const parts: ReactNode[] = [];
  let cursor = 0;
  filtered.forEach((m, i) => {
    if (m.match.start > cursor) parts.push(text.slice(cursor, m.match.start));
    if (m.kind === 'scripture') {
      const inStub = available.has(m.match.book.toLowerCase());
      parts.push(
        <ScriptureChip
          key={`s-${i}`}
          book={m.match.book}
          chapter={m.match.chapter}
          {...(m.match.verses ? { verses: m.match.verses } : {})}
          raw={m.match.raw}
          inStub={inStub}
        />,
      );
    } else {
      parts.push(
        <ConfessionChip
          key={`c-${i}`}
          source={m.match.source}
          ref_={m.match.ref}
          raw={m.match.raw}
        />,
      );
    }
    cursor = m.match.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

function ScriptureChip({
  book,
  chapter,
  verses,
  raw,
  inStub,
}: {
  book: string;
  chapter: number;
  verses?: string;
  raw: string;
  inStub: boolean;
}): JSX.Element {
  return (
    <Tooltip
      title={
        inStub
          ? `Abrir ${book} ${chapter}${verses ? `:${verses}` : ''}`
          : 'Libro aún no en el lector'
      }
      arrow
    >
      <Box
        component={RouterLink}
        to={buildScriptureHref(book, chapter, verses)}
        sx={{
          color: '#79c0ff',
          textDecoration: 'none',
          borderBottom: '1px dashed rgba(121,192,255,0.4)',
          '&:hover': { borderBottomStyle: 'solid' },
        }}
      >
        {raw}
      </Box>
    </Tooltip>
  );
}

function ConfessionChip({
  source,
  ref_,
  raw,
}: {
  source: string;
  ref_: string;
  raw: string;
}): JSX.Element {
  const entry = findConfession(source, ref_);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!entry) return <>{raw}</>;
  return (
    <>
      <Box
        component="span"
        onMouseEnter={(e) => setAnchor(e.currentTarget)}
        onMouseLeave={() => setAnchor(null)}
        sx={{
          display: 'inline-block',
          px: 0.75,
          borderRadius: 1,
          bgcolor: 'rgba(168,85,247,0.12)',
          color: '#c4b5fd',
          border: '1px solid rgba(168,85,247,0.35)',
          fontWeight: 600,
          fontSize: '0.85em',
          cursor: 'help',
          mx: 0.25,
        }}
      >
        {raw}
      </Box>
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={!!anchor}
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchor(null)}
        disableRestoreFocus
        PaperProps={{
          sx: {
            maxWidth: 420,
            p: 2,
            bgcolor: '#1f1734',
            border: '1px solid rgba(168,85,247,0.4)',
            borderRadius: 2,
          },
        }}
      >
        <Typography variant="caption" color="#c4b5fd" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
          {entry.sourceName.toUpperCase()} · {entry.ref}
        </Typography>
        <Typography variant="subtitle2" color="#f5f3ff" fontWeight={700} sx={{ mt: 0.5 }}>
          {entry.title}
        </Typography>
        <Typography variant="body2" color="#e9d5ff" sx={{ mt: 1, lineHeight: 1.6 }}>
          {entry.text}
        </Typography>
      </Popover>
    </>
  );
}
